import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, CheckCircle2, Lock } from 'lucide-react';

/* Unlisted route — accessible only by direct link + passcode. Same
   'evolvex' passcode as the pitch deck and roadmap. Once unlocked,
   auto-triggers the Levanta one-pager PDF download and shows a clean
   confirmation with a manual fallback link. Not in header, footer,
   sitemap, prerender or robots.txt (see public/robots.txt Disallow). */

const PDF_URL = '/downloads/Levanta-One-Pager.pdf';
const FILE_NAME = 'Levanta-One-Pager.pdf';
const PASSCODE = 'evolvex';
const SESSION_KEY = 'levanta_one_pager_unlocked';

const Gate = ({ onUnlock }) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 800);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0b1f3a] flex items-center justify-center px-6">
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md rounded-3xl bg-white/95 backdrop-blur shadow-2xl ring-1 ring-black/5 px-8 py-10"
        data-testid="one-pager-gate-form"
      >
        <div className="w-12 h-12 rounded-2xl bg-blue-500/15 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">
          Unlisted
        </p>
        <h1 className="text-3xl font-black text-[#0b1f3a] tracking-tight">
          Levanta — one-pager
        </h1>
        <p className="mt-3 text-sm text-[#0b1f3a]/65 leading-relaxed">
          This document is shared by invitation. Enter the passcode you
          received to download the PDF.
        </p>
        <input
          ref={inputRef}
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          aria-label="Passcode"
          data-testid="one-pager-gate-input"
          className={`mt-7 w-full px-4 py-3 rounded-xl bg-white ring-1 transition-colors text-[#0b1f3a] tracking-wider font-mono text-lg focus:outline-none ${
            error
              ? 'ring-red-400 focus:ring-red-500'
              : 'ring-black/10 focus:ring-blue-500'
          }`}
        />
        {error && (
          <p
            className="mt-3 text-xs text-red-600 font-medium"
            data-testid="one-pager-gate-error"
          >
            That passcode didn&rsquo;t match.
          </p>
        )}
        <button
          type="submit"
          data-testid="one-pager-gate-submit"
          className="mt-6 w-full px-6 py-3 rounded-xl bg-[#0b1f3a] text-white font-semibold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
        >
          Download the one-pager →
        </button>
      </form>
    </div>
  );
};

const DownloadPanel = () => {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      // 1. Force a save-to-disk download of the PDF.
      const a = document.createElement('a');
      a.href = PDF_URL;
      a.download = FILE_NAME;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // 2. Open the PDF in a new tab so the recipient can read it
      //    immediately. If the browser blocks the popup, the download
      //    above still succeeded and the manual "Download the PDF"
      //    button remains available as a fallback.
      window.open(PDF_URL, '_blank', 'noopener,noreferrer');
      setTriggered(true);
    }, 350);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      data-testid="one-pager-download-page"
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{
        background:
          'radial-gradient(1000px 500px at -200px -180px, rgba(47,107,255,0.10), transparent 60%),' +
          'radial-gradient(700px 400px at 110% 110%, rgba(255,184,74,0.10), transparent 55%),' +
          '#ffffff',
      }}
    >
      <div className="max-w-md w-full text-center">
        <div className="flex items-center justify-center mb-6">
          <img
            src="/wordmark-blue.png"
            alt="Levanta"
            className="h-9 w-auto object-contain"
            data-testid="one-pager-wordmark"
          />
        </div>

        <div className="mx-auto w-16 h-16 rounded-2xl bg-[#e8f0ff] flex items-center justify-center mb-5 ring-1 ring-[#2f6bff]/20">
          <FileText className="w-7 h-7 text-[#1a4bd8]" />
        </div>

        <h1 className="text-3xl font-black text-[#0b1f3a] tracking-tight leading-tight">
          Your one-pager is on its way.
        </h1>
        <p className="mt-3 text-sm text-[#0b1f3a]/65 leading-relaxed">
          The download should start automatically. If it doesn&apos;t, use
          the button below.
        </p>

        <a
          href={PDF_URL}
          download={FILE_NAME}
          data-testid="one-pager-download-link"
          className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0b1f3a] text-white font-semibold text-sm shadow-sm hover:shadow-md hover:bg-[#142e5c] transition-all"
        >
          <Download className="w-4 h-4" />
          Download the PDF
        </a>

        {triggered && (
          <p
            data-testid="one-pager-download-confirm"
            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 tracking-wide"
          >
            <CheckCircle2 className="w-4 h-4" />
            Download started
          </p>
        )}

        <p className="mt-8 text-[11px] font-mono uppercase tracking-[0.18em] text-[#0b1f3a]/40">
          Unlisted · Shared by link only
        </p>
      </div>
    </main>
  );
};

const OnePagerDownload = () => {
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1',
  );

  useEffect(() => {
    // Force noindex — belt-and-braces with server-side meta.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'Levanta — One-Pager';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  if (!unlocked) {
    return <Gate onUnlock={() => setUnlocked(true)} />;
  }
  return <DownloadPanel />;
};

export default OnePagerDownload;
